// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { forwardRef, type HTMLAttributes } from 'react';
import { TabsProvider, type TabsSize, type TabsVariant } from './tabs-context';
import { type TabsController, useTabsController } from './use-tabs-controller';

export interface TabsProps extends HTMLAttributes<HTMLDivElement> {
  readonly defaultValue?: string;
  readonly baseId?: string;
  readonly controller?: TabsController;
  readonly 'state-id'?: string;
  readonly controlled?: boolean;
  readonly variant?: TabsVariant;
  readonly size?: TabsSize;
  /** Called when the active tab changes. Use e.g. to sync with URL search params. */
  readonly onTabChange?: (value: string) => void;
}
export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      children,
      defaultValue,
      baseId,
      controller,
      'state-id': stateId,
      controlled,
      variant = 'pill',
      size = 'md',
      onTabChange,
      ...props
    },
    ref,
  ) => {
    const { id, setTab, tab } = useTabsController(
      defaultValue,
      stateId ?? controller?.stateId,
      onTabChange,
    );

    return (
      <TabsProvider
        baseId={baseId ?? controller?.id ?? id}
        onValueChange={controller?.setTab ?? setTab}
        value={controlled ? defaultValue : controller?.tab ?? tab}
        variant={variant}
        size={size}
      >
        <div {...props} ref={ref}>
          {children}
        </div>
      </TabsProvider>
    );
  },
);

Tabs.displayName = 'Tabs';
